import type { SupportedLocale } from "@/lib/localeCatalog";
import { DEFAULT_LOCALE, isPublicLocale } from "@/lib/localeCatalog";

export const LOCALE_PREF_COOKIE = "12go_locale";
export const LOCALE_PREF_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

const BOT_UA =
  /googlebot|bingbot|yandex(?:bot)?|baiduspider|duckduckbot|slurp|facebookexternalhit|twitterbot|linkedinbot|applebot|semrushbot|ahrefsbot|mj12bot|dotbot|petalbot|bytespider|gptbot|claudebot|ccbot|amazonbot|ia_archiver|pingdom|uptimerobot/i;

export function isCrawlerUserAgent(ua: string | null | undefined): boolean {
  if (!ua) return false;
  return BOT_UA.test(ua);
}

/** Cookie only counts for published locales. */
export function parseLocalePrefCookie(value: string | undefined | null): SupportedLocale | null {
  if (!value) return null;
  const short = value.trim().toLowerCase().split("-")[0];
  if (!isPublicLocale(short)) return null;
  return short;
}

/**
 * Picks the highest-q language from Accept-Language among PUBLIC locales.
 * Returns DEFAULT_LOCALE when nothing matches.
 */
export function localeFromAcceptLanguage(header: string | null | undefined): SupportedLocale {
  if (!header) return DEFAULT_LOCALE;

  const parts = header
    .split(",")
    .map((raw) => {
      const [tagPart, ...params] = raw.trim().split(";");
      const tag = (tagPart || "").trim().toLowerCase();
      let q = 1;
      for (const param of params) {
        const m = param.trim().match(/^q\s*=\s*([0-9.]+)$/i);
        if (m) q = Number(m[1]);
      }
      return { tag, q: Number.isFinite(q) ? q : 0 };
    })
    .filter((p) => p.tag)
    .sort((a, b) => b.q - a.q);

  for (const { tag } of parts) {
    const short = tag.split("-")[0];
    if (isPublicLocale(short)) return short;
  }

  return DEFAULT_LOCALE;
}

/**
 * Cookie wins over Accept-Language. Used only for unprefixed public URLs.
 */
export function resolveAutoLocale(
  cookieValue: string | undefined | null,
  acceptLanguage: string | null | undefined,
): SupportedLocale {
  return parseLocalePrefCookie(cookieValue) ?? localeFromAcceptLanguage(acceptLanguage);
}

export function localePrefCookieHeader(locale: SupportedLocale): string {
  return `${LOCALE_PREF_COOKIE}=${locale}; Path=/; Max-Age=${LOCALE_PREF_MAX_AGE}; SameSite=Lax`;
}

/** Client-side: remember explicit language choice (and URL locale). */
export function persistLocalePreference(locale: SupportedLocale): void {
  if (typeof document === "undefined") return;
  document.cookie = `${LOCALE_PREF_COOKIE}=${locale}; path=/; max-age=${LOCALE_PREF_MAX_AGE}; samesite=lax`;
}
